import {
  OPERATIONAL_PROFILES,
  SECURITY_ROLES,
} from '../../authorization/model.js';
import type { PlatformAdministrationModel } from './model.js';

export class PlatformAdministrationProvider {
  async load(identityToken: string): Promise<PlatformAdministrationModel> {
    return await this.request(identityToken, '/api/platform-admin', { method: 'GET' });
  }

  async updateOperationalProfile(
    identityToken: string,
    subjectId: string,
    operationalProfile: string,
  ): Promise<PlatformAdministrationModel> {
    return await this.request(
      identityToken,
      `/api/platform-admin/users/${encodeURIComponent(subjectId)}/profile`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operationalProfile }),
      },
    );
  }

  private async request(
    identityToken: string,
    path: string,
    init: RequestInit,
  ): Promise<PlatformAdministrationModel> {
    if (identityToken.length === 0) throw new Error('Authenticated session is unavailable');
    const response = await fetch(path, {
      ...init,
      headers: {
        ...init.headers,
        Authorization: `Bearer ${identityToken}`,
      },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Platform administration request failed');
    const payload = await response.json() as unknown;
    if (!isPlatformAdministrationModel(payload)) {
      throw new Error('Platform administration response is invalid');
    }
    return payload;
  }
}

function isPlatformAdministrationModel(value: unknown): value is PlatformAdministrationModel {
  if (!isRecord(value)
    || !Array.isArray(value.profiles)
    || !value.profiles.every((profile) =>
      (OPERATIONAL_PROFILES as readonly unknown[]).includes(profile))
    || !Array.isArray(value.users)
    || !Array.isArray(value.reservedSections)) return false;
  return value.users.every((user) => isRecord(user)
    && typeof user.subjectId === 'string'
    && (user.displayName === null || typeof user.displayName === 'string')
    && (user.email === null || typeof user.email === 'string')
    && (user.observedRole === null
      || (SECURITY_ROLES as readonly unknown[]).includes(user.observedRole))
    && (OPERATIONAL_PROFILES as readonly unknown[]).includes(user.operationalProfile)
    && (user.lastObservedAt === null || typeof user.lastObservedAt === 'string'));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
